export class Feedback {
  private audioContext: AudioContext | null = null
  private muted = false
  private lastDamageSoundAt = 0

  get isMuted(): boolean {
    return this.muted
  }

  async unlock(): Promise<void> {
    if (!this.audioContext) this.audioContext = new AudioContext()
    if (this.audioContext.state === 'suspended') await this.audioContext.resume()
  }

  toggleMute(): boolean {
    this.muted = !this.muted
    return this.muted
  }

  explosion(protectedTarget: boolean): void {
    this.vibrate(28)
    if (this.muted || !this.audioContext) return
    const now = this.audioContext.currentTime
    this.tone(protectedTarget ? 118 : 86, 32, 0.24, 0.13, 'sawtooth', now)
    this.tone(protectedTarget ? 244 : 180, 72, 0.13, 0.055, 'square', now + 0.018)
  }

  damage(amount: number): void {
    if (this.muted || !this.audioContext || amount < 30) return
    const now = this.audioContext.currentTime
    if (now - this.lastDamageSoundAt < 0.055) return
    this.lastDamageSoundAt = now
    this.tone(130 + Math.min(amount, 400) * 0.36, 72, 0.055, 0.025, 'triangle', now)
  }

  death(): void {
    this.vibrate([35, 20, 70])
    if (this.muted || !this.audioContext) return
    const now = this.audioContext.currentTime
    this.tone(210, 42, 0.32, 0.085, 'sawtooth', now)
    this.tone(310, 58, 0.2, 0.035, 'square', now + 0.045)
  }

  levelComplete(): void {
    this.vibrate([45, 30, 45, 30, 90])
    if (this.muted || !this.audioContext) return
    const now = this.audioContext.currentTime
    this.tone(220, 440, 0.18, 0.06, 'triangle', now)
    this.tone(330, 660, 0.24, 0.055, 'triangle', now + 0.14)
    this.tone(440, 880, 0.32, 0.05, 'triangle', now + 0.3)
  }

  private tone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    startAt: number,
  ): void {
    if (!this.audioContext) return
    const oscillator = this.audioContext.createOscillator()
    const gain = this.audioContext.createGain()
    oscillator.type = type
    oscillator.frequency.setValueAtTime(startFrequency, startAt)
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), startAt + duration)
    gain.gain.setValueAtTime(volume, startAt)
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)
    oscillator.connect(gain)
    gain.connect(this.audioContext.destination)
    oscillator.start(startAt)
    oscillator.stop(startAt + duration)
  }

  private vibrate(pattern: number | number[]): void {
    if (!this.muted && 'vibrate' in navigator) navigator.vibrate(pattern)
  }
}
